import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { permissionState, pushSupported, subscribeToPush, unsubscribeFromPush } from "../../lib/webPush";
import { useAuth } from "../../context/AuthContext";
import { Bell, BellOff, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CUSTOMER_TOPICS = [
  { key: "order_placed", label: "Order placed", desc: "Confirmation when your order is successful" },
  { key: "order_status", label: "Order status", desc: "Shipped, out for delivery, delivered" },
  { key: "back_in_stock", label: "Back in stock", desc: "Wishlisted items returning to stock" },
  { key: "new_reel", label: "New reels", desc: "Drops from creators you follow" },
];

const SELLER_TOPICS = [
  { key: "seller_new_order", label: "New orders", desc: "When a customer buys from your atelier" },
  { key: "seller_refund", label: "Refund requests", desc: "Refund initiated on one of your orders" },
];

const ADMIN_TOPICS = [
  { key: "admin_new_order", label: "All new orders", desc: "Every order placed on the platform" },
  { key: "admin_new_seller", label: "New seller signup", desc: "Seller awaiting approval" },
  { key: "admin_refund", label: "Refund requests", desc: "Any refund requested by a customer" },
];

export default function NotificationsTab() {
  const { user } = useAuth();
  const [perm, setPerm] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [prefsError, setPrefsError] = useState(null);

  const role = user?.role || "customer";
  const topicGroups = [
    { title: "Customer alerts", items: CUSTOMER_TOPICS },
    role === "seller" || role === "admin"
      ? { title: "Seller alerts", items: SELLER_TOPICS }
      : null,
    role === "admin" ? { title: "Admin alerts", items: ADMIN_TOPICS } : null,
  ].filter(Boolean);

  const refresh = async () => {
    setPerm(permissionState());
    try {
      if (pushSupported()) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(Boolean(sub));
      }
      const { data } = await api.get("/users/me/push-status");
      setPrefs(data.prefs);
      setPrefsError(null);
    } catch (e) {
      console.warn("notifications-load", e);
      setPrefsError(e?.response?.data?.detail || "Could not load notification preferences");
    }
  };

  useEffect(() => { refresh(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("Push notifications enabled");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Could not enable push");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      toast.success("Push notifications disabled");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to disable");
    } finally {
      setBusy(false);
    }
  };

  const togglePref = async (key, next) => {
    // Snapshot current state so rollback is race-safe (if two toggles fire
    // back-to-back, rolling back one by flipping `!next` would stomp the
    // other's optimistic value).
    const snapshot = prefs;
    setPrefs({ ...prefs, [key]: next });
    try {
      await api.put("/users/me/push-prefs", { [key]: next });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save preference");
      setPrefs(snapshot);
    }
  };

  if (!pushSupported()) {
    return (
      <div className="bg-white border border-[hsl(var(--kb-border))] p-6 max-w-2xl" data-testid="notif-tab">
        <h2 className="font-display text-2xl mb-2">Push notifications</h2>
        <p className="text-sm text-[hsl(var(--kb-muted))]">
          Your browser doesn't support web push notifications. Try opening this site in Chrome, Firefox, Edge or Safari to enable them.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="notif-tab">
      {/* Master toggle card */}
      <div className="bg-white border border-[hsl(var(--kb-border))] p-6">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${
            subscribed ? "bg-[hsl(var(--kb-primary))] text-white" : "bg-[hsl(var(--kb-surface-alt))]"
          }`}>
            {subscribed ? <Bell size={20} /> : <BellOff size={20} />}
          </div>
          <div className="flex-1">
            <div className="font-display text-2xl">
              Push notifications {subscribed ? "are on" : "are off"}
            </div>
            <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">
              Browser-native alerts — no SMS / WhatsApp involved. You can change topics below.
            </p>
            {perm === "denied" && (
              <div className="mt-3 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>Notifications are blocked at the browser level. Click the lock icon in your address bar and allow notifications, then reload the page.</span>
              </div>
            )}
            <div className="mt-4">
              {!subscribed ? (
                <button
                  onClick={enable}
                  disabled={busy || perm === "denied"}
                  className="kb-btn-primary"
                  data-testid="notif-enable-btn"
                >
                  {busy ? "Enabling…" : "Enable notifications"}
                </button>
              ) : (
                <button
                  onClick={disable}
                  disabled={busy}
                  className="kb-btn-outline"
                  data-testid="notif-disable-btn"
                >
                  {busy ? "Disabling…" : "Disable on this device"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Per-topic toggles */}
      {prefsError ? (
        <div
          className="bg-white border border-red-200 text-red-700 px-4 py-3 text-sm flex items-start gap-2 max-w-2xl"
          data-testid="notif-prefs-error"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{prefsError}</span>
        </div>
      ) : !prefs ? (
        <div
          className="bg-white border border-[hsl(var(--kb-border))] p-6 max-w-2xl animate-pulse"
          data-testid="notif-prefs-loading"
        >
          <div className="h-4 w-32 bg-[hsl(var(--kb-surface-alt))] mb-4" />
          <div className="space-y-3">
            <div className="h-3 w-full bg-[hsl(var(--kb-surface-alt))]" />
            <div className="h-3 w-3/4 bg-[hsl(var(--kb-surface-alt))]" />
            <div className="h-3 w-2/3 bg-[hsl(var(--kb-surface-alt))]" />
          </div>
        </div>
      ) : topicGroups.map((g) => (
        <div key={g.title} className="bg-white border border-[hsl(var(--kb-border))] p-6">
          <h3 className="font-display text-lg mb-4">{g.title}</h3>
          <div className="space-y-3">
            {g.items.map((t) => (
              <label
                key={t.key}
                className="flex items-start gap-3 cursor-pointer"
                data-testid={`notif-topic-${t.key}`}
              >
                <input
                  type="checkbox"
                  className="mt-1 cursor-pointer"
                  checked={Boolean(prefs[t.key])}
                  onChange={(e) => togglePref(t.key, e.target.checked)}
                  data-testid={`notif-toggle-${t.key}`}
                />
                <div className="flex-1">
                  <div className="text-sm font-medium flex items-center gap-1">
                    {t.label}
                    {prefs[t.key] && <CheckCircle2 size={12} className="text-green-600" />}
                  </div>
                  <div className="text-xs text-[hsl(var(--kb-muted))]">{t.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
